import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { parse, isValid } from "date-fns";
import { enGB } from "date-fns/locale";
import { cpf } from "cpf-cnpj-validator";

import { Phone } from "../../../../components/PhoneInput";
import { Input } from "../../../../components/Input";
import { Layout } from "../../../../components/Layout";
import { Container, Form, ContentForm, ContentButton } from "./styles";
import { IFormEditProfile } from "../../interfaces";
import { useAuth } from "../../../../hooks/useAuth";
import { Button } from "../../../../components/Button";
import React, { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../../hooks/useNotification";
import { HeaderTitle } from "../../../../components/HeaderTitle";
import { insertMask } from "../../../../utils/mask";
import { TypeMask } from "../../../../common/interfaces";
import { useTranslate } from "../../../../hooks/useTranslate";

const EditProfile = () => {
  const params = useParams();
  const { user, updateUserProfile } = useAuth();
  const { errorNotification, successNotification } = useNotification();
  const { translate } = useTranslate();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const schema = yup.object({
    name: yup.string().required(translate("global.name_required")),
    email: yup.string().required(translate("global.email_required")),
    phone: yup.string().required(translate("global.phone_required")),
    dob: yup.string().required(translate("global.dob_required")),
    cpf: yup.string().required(translate("profile.cpf_required")),
    office: yup.string().required(translate("global.office_required")),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<IFormEditProfile>({
    defaultValues: useMemo(
      () => ({
        name: user.name,
        email: user.email,
        phone: user.phone,
        dob: user.dob,
        cpf: user.cpf,
        office: user.office,
      }),
      [user]
    ),
    resolver: yupResolver(schema),
  });

  const submitForm = async ({
    phone,
    cpf,
    dob,
    email,
    name,
    office,
  }: IFormEditProfile) => {
    const data = { phone, cpf, date_birth: dob, email, name, office };
    try {
      setLoading(true);
      await updateUserProfile({ id: params.id!, data });
      setLoading(false);
      successNotification(translate("profile.success_message"));
      navigate("/profile");
    } catch {
      errorNotification(translate("profile.error_message"));
      setLoading(false);
    }
  };

  useEffect(() => {
    reset({
      name: user.name,
      email: user.email,
      phone: user.phone,
      dob: user.dob,
      cpf: user.cpf,
      office: user.office,
    });
  }, [user, reset]);

  const handleMask = (value: string, mask: TypeMask) => {
    switch (mask) {
      case "cpf": {
        if (!cpf.isValid(value)) {
          setError("cpf", {
            type: "pattern",
            message: translate("profile.error_cpf_invalid"),
          });
          return;
        }

        const valueFormatted = insertMask(value, mask);

        if (errors.cpf != null) clearErrors("cpf");
        setValue("cpf", valueFormatted);
        break;
      }
      case "dob": {
        value = insertMask(value, mask);

        const parsedDate = parse(value, "P", new Date(), { locale: enGB });
        const isValidDate = isValid(parsedDate);

        if (!isValidDate) {
          setError("dob", {
            type: "pattern",
            message: translate("profile.error_date_invalid"),
          });
        } else if (isValidDate && errors.dob != null) clearErrors("dob");

        setValue("dob", value);
        break;
      }
      default:
        return value;
    }
  };

  return (
    <Layout>
      <HeaderTitle
        title={translate("profile.title_edit")}
        showIcon
        url="/profile"
      />
      <Container>
        <Form onSubmit={handleSubmit(submitForm)}>
          <p>{translate("profile.title_form")}</p>
          <ContentForm>
            <Input
              {...register("name")}
              error={errors.name?.message}
              placeholder={translate("global.placeholder_name")}
              type="text"
            />
            <Input
              {...register("email")}
              error={errors.email?.message}
              placeholder={translate("global.placeholder_email")}
              type="email"
            />
            <Controller
              name="phone"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Phone
                  placeholder={translate("global.placeholder_phone")}
                  value={value}
                  onChange={onChange}
                  defaultCountry="BR"
                  error={errors.phone?.message}
                />
              )}
            />
            <Input
              {...register("dob")}
              error={errors.dob?.message}
              placeholder={translate("global.placeholder_dob")}
              type="date"
              // onChange={(e) => handleMask(e.target.value, 'dob')}
            />
            <Input
              {...register("cpf")}
              error={errors.cpf?.message}
              placeholder={translate("profile.placeholder_cpf")}
              onChange={(e) => handleMask(e.target.value, "cpf")}
              type="text"
            />
            <Input
              {...register("office")}
              error={errors.office?.message}
              placeholder={translate("global.placeholder_office")}
              type="text"
            />
          </ContentForm>
          <ContentButton>
            <Button
              isLoading={loading}
              type="submit"
              label={translate("profile.btn_save")}
            />
          </ContentButton>
        </Form>
      </Container>
    </Layout>
  );
};

export { EditProfile };
